import { Autocomplete, Checkbox, FormControlLabel, FormHelperText, Grid } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import allTimezones from "lib/timezone_constants";
import moment from "moment/moment";
import React, { useEffect, useState } from "react";
import CustomRepeatIntervalModal from "./CustomRepeatIntervalModal";

const repeatIntervalOptions = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Annually", value: "annually" },
  { label: "Custom", value: "custom" },
];

function Duration({ formData }) {
  const { values, errors, touched, setFieldValue } = formData;
  const { startDate: startDateV, endDate: endDateV, timezone: timezoneV } = values.duration;

  const [isCustomRepeatIntervalModalOpen, setIsCustomRepeatIntervalModalOpen] = useState(false);

  useEffect(() => {
    moment.tz.setDefault(timezoneV);
  }, [timezoneV]);

  // eslint-disable-next-line consistent-return
  const generateHelperTextForStartDate = () => {
    if (startDateV && touched?.duration?.startDate && errors?.duration?.startDate) {
      return errors?.duration?.startDate;
    }
    if (!startDateV && touched?.duration?.startDate && errors?.duration?.startDate) {
      return "Start Time is required";
    }
    if (!startDateV && touched?.duration?.endDate && errors?.duration?.endDate) {
      return "Start Time is required";
    }
  };

  // eslint-disable-next-line consistent-return
  const startDateHasError = () => {
    if (touched?.duration?.startDate && Boolean(errors?.duration?.startDate)) {
      return true;
    }
    if (!startDateV && touched?.duration?.startDate && Boolean(errors?.duration?.startDate)) {
      return true;
    }
    if (!startDateV && touched?.duration?.endDate && Boolean(errors?.duration?.endDate)) {
      return true;
    }
  };

  const handleCloseCustomRepeatIntervalModal = () => {
    setIsCustomRepeatIntervalModalOpen(false);
  };

  return (
    <Grid container spacing={3} p={1}>
      <Grid item xs={12}>
        <MDBox>
          <MDTypography variant="h5" fontWeight="bold">
            Duration
          </MDTypography>
          <MDTypography variant="subtitle2" sx={{ color: "#7b809a" }}>
            When should the competition take place?
          </MDTypography>
        </MDBox>
      </Grid>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Grid item sm={12} md={6}>
          <DateTimePicker
            label="Start Time"
            placeholder="Start Time"
            disablePast
            value={startDateV || null}
            minDateTime={moment.tz(undefined, timezoneV).add(15, "minutes")}
            onChange={(value) => {
              setFieldValue("duration.startDate", value || null);
            }}
            slotProps={{
              textField: {
                variant: "standard",
                fullWidth: true,
                error: startDateHasError(),
                helperText: generateHelperTextForStartDate(),
              },
            }}
            viewRenderers={{
              hours: null,
              minutes: null,
              seconds: null,
            }}
          />
        </Grid>
        <Grid item sm={12} md={6}>
          <DateTimePicker
            label="End Time"
            placeholder="End Time"
            disablePast
            value={endDateV || null}
            minDateTime={
              !startDateV
                ? moment.tz(undefined, timezoneV)
                : moment.tz(startDateV, timezoneV)?.add(5, "minutes")
            }
            onChange={(value) => {
              setFieldValue("duration.endDate", value || null);
            }}
            viewRenderers={{
              hours: null,
              minutes: null,
              seconds: null,
            }}
            slotProps={{
              textField: {
                variant: "standard",
                fullWidth: true,
                error: touched?.duration?.endDate && Boolean(errors?.duration?.endDate),
                helperText: touched?.duration?.endDate && errors?.duration?.endDate,
              },
            }}
          />
        </Grid>
      </LocalizationProvider>
      <Grid item xs={12} md={6}>
        <Autocomplete
          fullWidth
          disableClearable
          getOptionLabel={(option) =>
            `${option?.value || ""} ${
              option?.title?.slice(0, option.title.indexOf(")") + 1) || ""
            }`.trim()
          }
          value={allTimezones.find((_) => _.value === timezoneV) || null}
          onChange={(_, selectedTimezone) => {
            setFieldValue("duration.timezone", selectedTimezone.value);
            if (startDateV) {
              setFieldValue("duration.startDate", null);
            }
            if (endDateV) {
              setFieldValue("duration.endDate", null);
            }
          }}
          options={allTimezones}
          renderInput={(params) => (
            <MDInput
              variant="standard"
              {...params}
              label="Select timezone"
              error={touched?.duration?.timezone && Boolean(errors?.duration?.timezone)}
              helperText={touched?.duration?.timezone && errors?.duration?.timezone}
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
        />
        {touched?.duration?.timezone && errors?.duration?.timezone ? (
          <FormHelperText>{errors.duration.timezone}</FormHelperText>
        ) : null}
      </Grid>
      <Grid
        item
        sm={12}
        md={6}
        sx={{
          "& .MuiFormControlLabel-root": {
            display: "flex",
            mr: 0,
          },
          "& .MuiTypography-root ": {
            fontSize: "1rem",
          },
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={values?.duration?.repeatCompetition}
              onChange={(_, checked) => setFieldValue("duration.repeatCompetition", checked)}
            />
          }
          label="Repeat Competition"
        />
        {values?.duration?.repeatCompetition && (
          <MDBox mt={3}>
            <Autocomplete
              fullWidth
              disableClearable
              value={
                values?.duration?.repeatCompetition
                  ? repeatIntervalOptions.find((_) => _.value === values.duration.repeatInterval) ??
                    null
                  : null
              }
              onChange={(_, selectedInterval) => {
                if (selectedInterval.value === "custom") setIsCustomRepeatIntervalModalOpen(true);
                setFieldValue("duration.repeatInterval", selectedInterval.value);
              }}
              options={repeatIntervalOptions}
              renderInput={(params) => (
                <MDInput
                  variant="standard"
                  {...params}
                  label="Repeat interval"
                  error={
                    touched?.duration?.repeatCompetition &&
                    Boolean(errors?.duration?.repeatInterval)
                  }
                  helperText={
                    touched?.duration?.repeatCompetition && errors?.duration?.repeatInterval
                  }
                />
              )}
            />
            {touched?.duration?.repeatCompetition && errors?.duration?.repeatInterval ? (
              <FormHelperText>{errors?.duration?.repeatInterval}</FormHelperText>
            ) : null}
          </MDBox>
        )}
      </Grid>
      <CustomRepeatIntervalModal
        open={isCustomRepeatIntervalModalOpen}
        handleClose={handleCloseCustomRepeatIntervalModal}
        timezoneV={timezoneV}
        startDateV={startDateV}
        setFieldValue={setFieldValue}
        values={values?.duration?.customRepeatInterval}
      />
    </Grid>
  );
}

export default Duration;
